import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Drawer, IconButton, Divider } from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import { useTranslation } from "react-i18next";
import StatusDropdown, {
  getAttendanceStatusDotColor,
  getAttendanceStatusPinMarkerIcon,
} from "../../../../components/StatusDropdown";
import CustomButton from "../../../../components/CustomButton";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import {
  convertUTCToLocalDateTime,
  formatDurationMinutes,
} from "../../../../components/dateTimeUtils";
import {
  getDayClassificationStatus,
  getPunchActivityStatus,
} from "./monthlyAttendanceUtils";

const defaultCenter = { lat: 37.0902, lng: -95.7129 };
const defaultZoom = 13;
const fallbackZoom = 4;
const PIN_BY_ACTION = {
  CLOCK_IN:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  BREAK_START:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
  BREAK_END:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
  CLOCK_OUT:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
};
function deriveScheduleMatchValid(scheduleStatus) {
  if (!scheduleStatus || scheduleStatus.hasSchedule === false) return null;
  const c = scheduleStatus.classification || "";
  if (c === "NOT_APPLICABLE") return true;
  if (scheduleStatus.isOnTime === true) return true;
  if (scheduleStatus.isEarly === true || scheduleStatus.isLate === true) return false;
  if (c.includes("EARLY") || c.includes("LATE")) return false;
  if (c.includes("ON_TIME")) return true;
  return null;
}

function formatMinutesVsScheduleText(scheduleStatus, t) {
  const mins = scheduleStatus?.minutesFromSchedule;
  if (mins == null || scheduleStatus?.classification === "NOT_APPLICABLE") return null;
  const abs = Math.round(Math.abs(mins));
  const duration = formatDurationMinutes(abs) ?? `${abs} min`;
  const ref = scheduleStatus?.scheduleReference?.comparedWith;
  if (ref === "scheduled_start") {
    if (mins < 0) return t("attendance.todayPunchDetails.minutesBeforeScheduledStart", { duration });
    if (mins > 0) return t("attendance.todayPunchDetails.minutesAfterScheduledStart", { duration });
  }
  if (ref === "scheduled_end") {
    if (mins < 0) return t("attendance.todayPunchDetails.minutesBeforeScheduledEnd", { duration });
    if (mins > 0) return t("attendance.todayPunchDetails.minutesAfterScheduledEnd", { duration });
  }
  return duration;
}

/** e.g. face_not_matched → Face Not Matched */
function formatIssueKeyToTitleCase(key) {
  if (key == null || key === "") return "";
  return String(key)
    .trim()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function formatIssueKeysList(keys) {
  if (!keys?.length) return null;
  return keys.map((key) => formatIssueKeyToTitleCase(key)).join(", ");
}

function parseClockDurationToMinutes(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return Math.floor(value);
  const s = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(Number(s));
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.length >= 2 && parts.every((p) => Number.isFinite(p))) {
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  }
  return null;
}

function hoursDecimalToMinutes(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 60);
}

function resolveDaySchedule(record, ctx) {
  return (
    record?.schedule ??
    record?.details?.schedule ??
    ctx?.schedule_info?.schedule ??
    ctx?.schedule_info?.details?.schedule ??
    ctx?.schedule ??
    ctx?.details?.schedule ??
    null
  );
}

function resolveDepartmentName(record, ctx) {
  const dept =
    record?.department ??
    record?.details?.department ??
    ctx?.department ??
    ctx?.schedule_info?.department ??
    ctx?.schedule_info?.details?.department;
  if (typeof dept === "string" && dept.trim()) return dept.trim();
  if (dept && typeof dept === "object") {
    const name = dept.name ?? dept.department_name;
    if (name != null && String(name).trim()) return String(name).trim();
  }
  const flat =
    record?.department_name ??
    record?.details?.department_name ??
    ctx?.department_name;
  return flat != null && String(flat).trim() ? String(flat).trim() : null;
}

function formatAttendanceIssuesSummary(record) {
  const fromDetails = record?.details?.attendance_issues;
  if (Array.isArray(fromDetails) && fromDetails.length > 0) {
    const labels = fromDetails.map(
      (issue) => issue.label || formatIssueKeyToTitleCase(issue.type)
    );
    return [...new Set(labels.filter(Boolean))].join(", ");
  }
  const issueList = record?.issues?.issue_list;
  if (Array.isArray(issueList) && issueList.length > 0) {
    return formatIssueKeysList(issueList);
  }
  return null;
}

const PUNCH_ISSUE_FLAG_KEYS = [
  "face_not_matched",
  "location_not_matched",
  "requires_review",
  "late_clock_in",
  "early_clock_in",
  "early_clock_out",
  "late_clock_out",
];

const VALIDATION_ISSUE_KEYS = new Set([
  "face_not_matched",
  "location_not_matched",
  "requires_review",
]);

const SCHEDULE_ISSUE_KEYS = new Set([
  "late_clock_in",
  "early_clock_in",
  "early_clock_out",
  "late_clock_out",
]);

/** Monthly API: punch.issues; Today/details API: punch.punch.issues */
function getPunchIssues(punch) {
  return punch?.punch?.issues ?? punch?.issues ?? null;
}

function getPunchIssueList(punchIssues) {
  if (!punchIssues) return [];
  if (Array.isArray(punchIssues.issue_list) && punchIssues.issue_list.length > 0) {
    return punchIssues.issue_list;
  }
  return PUNCH_ISSUE_FLAG_KEYS.filter((key) => punchIssues[key] === true);
}

function formatIssuesFromPunch(punch) {
  return formatIssueKeysList(getPunchIssueList(getPunchIssues(punch)));
}

/** `YYYY-MM-DD` for schedule boundaries (monthly `record.date` is often a JS Date string). */
function extractPunchScheduleYmd(record, punch) {
  if (typeof punch?.occurred_at === "string") {
    const fromOccurred = punch.occurred_at.match(/^(\d{4}-\d{2}-\d{2})/);
    if (fromOccurred) return fromOccurred[1];
  }
  if (typeof punch?.punch_date === "string") {
    const fromPunchDate = String(punch.punch_date).match(/^(\d{4}-\d{2}-\d{2})/);
    if (fromPunchDate) return fromPunchDate[1];
  }
  const raw = record?.date;
  if (!raw) return null;
  const direct = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return null;
}

/** Monthly `punches[]`: build scheduleStatus for minutes-vs-schedule (clock in/out only). */
function resolveMonthlyPunchScheduleStatus(punch, actionType, record, ctx) {
  const fromRecord = punch?.record?.scheduleStatus ?? punch?.record?.schedule_status;
  if (fromRecord?.scheduleReference && fromRecord?.classification) {
    return fromRecord;
  }

  const isClockIn = actionType === "CLOCK_IN" || actionType === "CHECK_IN";
  const isClockOut = actionType === "CLOCK_OUT" || actionType === "CHECK_OUT";
  if (!isClockIn && !isClockOut) {
    return fromRecord || null;
  }

  const timing = punch?.schedule_timing;
  const timeDiff = getPunchIssues(punch)?.time_difference ?? punch?.issues?.time_difference;
  const classification = String(
    timeDiff?.classification ?? timing?.classification ?? fromRecord?.classification ?? ""
  ).trim();
  if (!classification || classification === "NOT_APPLICABLE") {
    return fromRecord || null;
  }

  const minutesFromSchedule =
    timeDiff?.minutesFromSchedule ??
    timeDiff?.minutes_from_schedule ??
    timing?.minutesFromSchedule ??
    timing?.minutes_from_schedule ??
    fromRecord?.minutesFromSchedule;
  if (minutesFromSchedule == null) {
    return fromRecord || null;
  }

  const comparedWith = isClockOut ? "scheduled_end" : "scheduled_start";
  const scheduleDate = extractPunchScheduleYmd(record, punch);

  return {
    ...(fromRecord || {}),
    hasSchedule: true,
    classification,
    minutesFromSchedule,
    isEarly: timing?.isEarly ?? timing?.is_early ?? fromRecord?.isEarly,
    isLate: timing?.isLate ?? timing?.is_late ?? fromRecord?.isLate,
    isOnTime: timing?.isOnTime ?? timing?.is_on_time ?? fromRecord?.isOnTime,
    scheduleReference: {
      ...(fromRecord?.scheduleReference || {}),
      date: scheduleDate,
      timezone: ctx?.timezone ?? "UTC",
      scheduledStart:
        timing?.scheduleStart ??
        timing?.schedule_start ??
        timeDiff?.scheduleStart ??
        timeDiff?.schedule_start,
      scheduledEnd:
        timing?.scheduleEnd ??
        timing?.schedule_end ??
        timeDiff?.scheduleEnd ??
        timeDiff?.schedule_end,
      comparedWith,
      graceMinutes:
        timing?.graceMinutes ??
        timing?.grace_minutes ??
        timeDiff?.graceMinutes ??
        timeDiff?.grace_minutes,
    },
  };
}

const SectionHeader = ({ label }) => (
  <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 18,
        fontWeight: 600,
        color: "#000000",
      }}
    >
      {label}
    </CustomText>
  </Box>
);

const InfoBox = ({ children }) => (
  <Box
    mt={2}
    sx={{
      border: "1px solid #091E4224",
      borderRadius: "10px",
      px: 2,
      py: 2,
    }}
  >
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => {
  const isElement = React.isValidElement(value);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
        gap: 1,
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 15,
          fontWeight: 400,
          color: "#5E5C5C",
          textAlign: "left",
          flexShrink: 0,
        }}
      >
        {label}
      </CustomText>
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        {isElement ? (
          value
        ) : (
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 15,
              fontWeight: 400,
              color: "#172B4D",
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {value === undefined || value === null || value === "" ? "--" : value}
          </CustomText>
        )}
      </Box>
    </Box>
  );
};

const EvidenceThumb = ({ src, alt }) => (
  <Box
    sx={{
      width: 112,
      height: 100,
      borderRadius: "10px",
      border: "1px solid #E4E6EF",
      backgroundColor: "#F8F9FB",
      overflow: "hidden",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {src ? (
      <img
        src={src}
        alt={alt}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        crossOrigin="anonymous"
      />
    ) : (
      <CustomText sx={{ ...textStyles.h1, fontSize: 14, color: "#6B778C" }}>--</CustomText>
    )}
  </Box>
);

const ValidationBadge = ({ isValid, t }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 92,
      px: 1.5,
      py: 0.4,
      borderRadius: "4px",
      border: `1px solid ${isValid ? "#1F845A55" : "#DE350B55"}`,
      backgroundColor: isValid ? "#E3FCEF" : "#FFEBE6",
      color: isValid ? "#1F845A" : "#AE2A19",
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.4,
    }}
  >
    {isValid ? t("summaryDetails.valid") : t("summaryDetails.invalid")}
  </Box>
);

export default function AttendanceDetailsDrawer({ open, onClose, selectedRow, monthlyApiData }) {
  const { t } = useTranslation();
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const previewMapRef = useRef(null);
  const fullMapRef = useRef(null);
  const record = selectedRow?.raw;
  const ctx = monthlyApiData;
  const settings = ctx?.attendanceSettings;
  const daySchedule = resolveDaySchedule(record, ctx);
  const schedRoot = daySchedule ?? ctx?.schedule ?? ctx?.details?.schedule;




  const normalizeHms = (time) => {
    const parts = String(time || "").split(":");
    const h = String(parts[0] || "00").padStart(2, "0");
    const m = String(parts[1] || "00").padStart(2, "0");
    const s = String(parts[2] || "00").padStart(2, "0");
    return `${h}:${m}:${s}`;
  };
  
  const getYmd = (value) => {
    if (!value) return null;
  
    const direct = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (direct) return direct[1];
  
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return convertUTCToLocalDateTime(parsed.toISOString(), {
        format: "YYYY-MM-DD",
      });
    }
  
    return null;
  };
  
  const formatUtcScheduleTime = (dateValue, timeValue) => {
    const ymd = getYmd(dateValue);
    if (!ymd || !timeValue) return "--";
  
    const value = convertUTCToLocalDateTime(`${ymd}T${normalizeHms(timeValue)}Z`, {
      format: "h:mm a",
    });
  
    return value === "—" ? "--" : value;
  };



  const formatDateTime = (dateString) => {
    const formattedValue = convertUTCToLocalDateTime(dateString, {
      formatOptions: {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      },
    });
    return formattedValue === "—" ? "--" : formattedValue;
  };

  const sortedPunches = useMemo(
    () =>
      [...(record?.punches || [])].sort(
        (a, b) => new Date(b?.occurred_at || 0) - new Date(a?.occurred_at || 0)
      ),
    [record]
  );

  const clockInPunches = useMemo(
    () => sortedPunches.filter((p) => p.action_type === "CLOCK_IN"),
    [sortedPunches]
  );
  const clockOutPunches = useMemo(
    () => sortedPunches.filter((p) => p.action_type === "CLOCK_OUT"),
    [sortedPunches]
  );
  const lastClockInPunch = clockInPunches.length > 0 ? clockInPunches[0] : null;
  const lastClockOutPunch = clockOutPunches.length > 0 ? clockOutPunches[0] : null;
  const lastActionPunch = sortedPunches.length > 0 ? sortedPunches[0] : null;

  const formatActionType = (actionType) => {
    if (!actionType) return "--";
    return String(actionType)
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getActionLabel = (actionType) =>
    t(`attendance.actionType.${actionType}`, {
      defaultValue: formatActionType(actionType),
    });

  const getAttendanceIssueLabel = (classification) => {
    if (!classification || classification === "NOT_APPLICABLE") {
      return "--";
    }
    return t(`attendance.issueClassification.${classification}`, {
      defaultValue: formatActionType(classification),
    });
  };

  const getMarkerInfoLabel = (marker) => {
    const { statusKey, actionType } = marker;
    if (statusKey && statusKey !== actionType && statusKey !== "NOT_APPLICABLE") {
      return getAttendanceIssueLabel(statusKey);
    }
    return getActionLabel(actionType);
  };

  const mapAttendanceReviewStatus = (reviewStatus) => {
    if (!reviewStatus) return reviewStatus;
    const normalizedStatus = String(reviewStatus).trim().toLowerCase().replace(/\s+/g, "_");
    if (normalizedStatus === "pending") return "pendingPunch";
    if (["approved", "validated", "valid"].includes(normalizedStatus)) return "validatedPunch";
    if (["rejected", "invalid", "has_issue", "has_issues"].includes(normalizedStatus)) {
      return "invalidPunch";
    }
    return reviewStatus;
  };

  const getPunchLocationText = (punch) =>
    punch?.record?.locationData?.textDescription ||
    punch?.punch?.location?.locationText ||
    punch?.location?.location_text ||
    (punch?.location?.gps_latitude && punch?.location?.gps_longitude
      ? `${punch.location.gps_latitude}, ${punch.location.gps_longitude}`
      : null);

  const summaryLocationText = lastActionPunch
    ? getPunchLocationText(lastActionPunch) || "--"
    : "--";

  const markers = useMemo(
    () =>
      sortedPunches
        .map((p, i) => {
          const lat = parseFloat(
            p.record?.locationData?.gps?.latitude ?? p.location?.gps_latitude
          );
          const lng = parseFloat(
            p.record?.locationData?.gps?.longitude ?? p.location?.gps_longitude
          );
          if (!isFinite(lat) || !isFinite(lng)) return null;
          const actionType = p.action_type;
          return {
            id: p.id || i,
            position: { lat, lng },
            actionType,
            statusKey: getPunchActivityStatus(p) || actionType,
            time: formatDateTime(p.occurred_at),
            locationText: getPunchLocationText(p) || "--",
          };
        })
        .filter(Boolean),
    [sortedPunches]
  );

  const getMarkerIcon = useCallback((actionType, statusKey) => {
    const pinIcon = getAttendanceStatusPinMarkerIcon(statusKey, actionType);
    if (pinIcon?.url) return pinIcon;
    const markerUrl = PIN_BY_ACTION[actionType] || PIN_BY_ACTION.CLOCK_IN;
    return { url: markerUrl };
  }, []);

  const fitMapToMarkers = useCallback((map) => {
    if (!map || !window.google) return;
    if (markers.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(fallbackZoom);
      return;
    }
    if (markers.length === 1) {
      map.setCenter(markers[0].position);
      map.setZoom(defaultZoom);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend(marker.position));
    map.fitBounds(bounds, 40);
  }, [markers]);

  useEffect(() => {
    fitMapToMarkers(previewMapRef.current);
    fitMapToMarkers(fullMapRef.current);
  }, [fitMapToMarkers, isFullMapOpen]);

  useEffect(() => {
    if (activeMarkerId && !markers.some((marker) => marker.id === activeMarkerId)) {
      setActiveMarkerId(null);
    }
  }, [markers, activeMarkerId]);

  const scheduledWorkingMinutes =
    schedRoot?.scheduledWorkingMinutes ??
    hoursDecimalToMinutes(schedRoot?.scheduledWorkingHours) ??
    hoursDecimalToMinutes(settings?.schedule?.dailyHours ?? schedRoot?.dailyHours);
  const scheduledHoursStr = formatDurationMinutes(scheduledWorkingMinutes) ?? "--";

  const completedWorkingMinutes =
    schedRoot?.completedWorkingMinutes ??
    record?.gross_working_minutes ??
    hoursDecimalToMinutes(record?.working_hours);
  const workingHoursDayStr = formatDurationMinutes(completedWorkingMinutes) ?? "--";

  const breakAllowMin =
    schedRoot?.breakDurationMinutes ??
    schedRoot?.breakPolicy?.unpaid ??
    settings?.schedule?.breakPolicy?.unpaid ??
    ctx?.schedule_info?.schedule?.breakDurationMinutes ??
    ctx?.schedule_info?.schedule?.breakPolicy?.unpaid;
  const breakAllowStr = formatDurationMinutes(breakAllowMin) ?? "--";

  const breakTakenMinutes =
    record?.details?.takenBreakMinutes ??
    schedRoot?.takenBreakMinutes ??
    parseClockDurationToMinutes(record?.details?.breakTakenTime) ??
    record?.break_time_minutes;
  const breakTakenDay = formatDurationMinutes(breakTakenMinutes) ?? "--";

  const scheduledWindowText = (() => {
    const details = record?.details;
    const scheduleInfo = ctx?.schedule_info;
    const startH =
      details?.scheduledStart ??
      schedRoot?.startTime ??
      scheduleInfo?.scheduled_start ??
      scheduleInfo?.schedule?.startTime ??
      settings?.schedule?.startTime;
    const endH =
      details?.scheduledEnd ??
      schedRoot?.endTime ??
      scheduleInfo?.scheduled_end ??
      scheduleInfo?.schedule?.endTime ??
      settings?.schedule?.endTime;
    const grace =
      details?.gracePeriod ??
      schedRoot?.graceMinutes ??
      scheduleInfo?.grace_period_minutes ??
      scheduleInfo?.schedule?.graceMinutes ??
      settings?.schedule?.graceMinutes;

    if (!startH || !endH) return null;

    const scheduleDate =
      record?.date ||
      lastClockInPunch?.record?.scheduleStatus?.scheduleReference?.date ||
      lastClockInPunch?.occurred_at ||
      ctx?.date_range?.start;

    return t("attendance.todayPunchDetails.scheduledWindow", {
      start: formatUtcScheduleTime(scheduleDate, startH),
      end: formatUtcScheduleTime(scheduleDate, endH),
      grace: grace ?? "--",
    });
  })();

  const workingDaysStr = (() => {
    const days =
      schedRoot?.workingDays ??
      schedRoot?.work_days ??
      ctx?.schedule_info?.work_days ??
      settings?.schedule?.workingDays;
    return Array.isArray(days) && days.length > 0 ? days.join(", ") : "--";
  })();

  const issuesDetailsStr = formatAttendanceIssuesSummary(record);
  const departmentName = resolveDepartmentName(record, ctx);

  const dayClassificationStatus =
    getDayClassificationStatus(record) || mapAttendanceReviewStatus(record?.status);

  const issues = record?.issues || {};
  const dayIssueCount = Number(
    record?.total_punch_issue_count ??
      issues.total_punch_issue_count ??
      selectedRow?.issuesCount ??
      0
  );

  if (!selectedRow) return null;

  const headerDate =
    record?.date &&
    new Date(
      String(record.date).length === 10 ? `${record.date}T12:00:00` : record.date
    ).toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 550 }, borderRadius: 0 },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {headerDate || "--"}
          </CustomText>
        </Box>
      </Box>
      <Divider />

      <Box sx={{ px: 2, py: 2, overflowY: "auto" }}>
        <SectionHeader label={t("summaryDetails.summary")} />
        <InfoBox>
          {ctx?.worker ? (
            <InfoRow label={t("attendanceDrawer.workerName")} value={ctx.worker.fullName || "--"} />
          ) : null}
          {ctx?.company?.name ? (
            <InfoRow label={t("summaryDetails.companyName")} value={ctx.company.name} />
          ) : null}
          {departmentName ? (
            <InfoRow label={t("summaryDetails.department")} value={departmentName} />
          ) : null}
               <InfoRow
            label={t("summaryDetails.status")}
            value={
              <Box sx={{ pointerEvents: "none", display: "flex", justifyContent: "flex-end" }}>
                <StatusDropdown currentStatus={dayClassificationStatus} />
              </Box>
            }
          />
              <InfoRow
            label={t("summaryDetails.checkIn")}
            value={formatDateTime(lastClockInPunch?.occurred_at)}
          />
          <InfoRow
            label={t("summaryDetails.checkOut")}
            value={formatDateTime(lastClockOutPunch?.occurred_at)}
          />
          <InfoRow label={t("summaryDetails.location")} value={summaryLocationText} />
            <InfoRow
            label={t("attendance.todayPunchDetails.scheduledWorkingHours")}
            value={scheduledHoursStr}
          />
                    <InfoRow label={t("attendanceDrawer.workingHours")} value={workingHoursDayStr} />

          
       
        
          <InfoRow
            label={t("attendance.todayPunchDetails.scheduledBreakAllowance")}
            value={breakAllowStr}
          />
          <InfoRow label={t("attendance.todayPunchDetails.breakTimeTaken")} value={breakTakenDay} />
     
      
          <InfoRow label={t("summaryDetails.totalPunches")} value={record?.total_punches ?? "--"} />
          <InfoRow
            label={t("summaryDetails.totalIssueCount")}
            value={String(dayIssueCount)}
          />
          {issuesDetailsStr ? (
            <InfoRow label={t("issuesDetails")} value={issuesDetailsStr} />
          ) : null}
           {scheduledWindowText ? (
            <InfoRow
              label={t("attendance.todayPunchDetails.scheduledWindowLabel")}
              value={scheduledWindowText}
            />
          ) : null}
        </InfoBox>
       
        <SectionHeader label={t("summaryDetails.punches")} />
        {sortedPunches.map((punch) => {
          const compRec = punch.record;
          const punchLite = punch.punch;
          const actionType = compRec?.actionType || punch.action_type;
          const isClockIn = actionType === "CLOCK_IN" || actionType === "CHECK_IN";
          const isClockOut = actionType === "CLOCK_OUT" || actionType === "CHECK_OUT";
          const scheduleStatus = resolveMonthlyPunchScheduleStatus(
            punch,
            actionType,
            record,
            ctx
          );
          const classification =
            scheduleStatus?.classification || punch.schedule_timing?.classification;
          const scheduleMatchValid = deriveScheduleMatchValid(scheduleStatus);
          const minutesVsText = formatMinutesVsScheduleText(scheduleStatus, t);
          const showMinutesVsSchedule = (isClockIn || isClockOut) && Boolean(minutesVsText);
          const showClockScheduleRows =
            (isClockIn || isClockOut) &&
            classification &&
            classification !== "NOT_APPLICABLE" &&
            !String(classification).includes("ON_TIME") &&
            scheduleStatus?.scheduleReference;
          const punchIssues = getPunchIssues(punch);
          const punchIssueList = getPunchIssueList(punchIssues);
          const issuesFromValidation = compRec?.validation?.validationIssues?.length
            ? compRec.validation.validationIssues.map((v) => v.message).filter(Boolean).join(", ")
            : null;
          const issuesLine = formatIssuesFromPunch(punch) || issuesFromValidation;
          const issueCountDisplay =
            punchIssues?.issue_count ??
            (punchIssueList.length > 0 ? punchIssueList.length : null) ??
            compRec?.validation?.validationIssues?.length ??
            null;
          const validationIssueList =
            punchLite?.validation_issues?.issue_list ??
            punchIssueList.filter((key) => VALIDATION_ISSUE_KEYS.has(key));
          const scheduleIssueList =
            punchLite?.schedule_issues?.issue_list ??
            punchIssueList.filter((key) => SCHEDULE_ISSUE_KEYS.has(key));
          const hasPunchIssues =
            punchIssues?.has_any_issue === true ||
            punchIssueList.length > 0 ||
            Number(issueCountDisplay) > 0;

          // const punchTimeDisplay = (() => {
          //   if (punchLite?.formattedDateTime) return punchLite.formattedDateTime;
          //   const v = convertUTCToLocalDateTime(punch.occurred_at, {
          //     formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
          //   });
          //   return v === "—" ? "--" : v;
          // })();


          const punchTimeDisplay = formatDateTime(punch.occurred_at);

          const primaryTimeLabel = (() => {
            if (
              (actionType === "CLOCK_IN" || actionType === "CLOCK_OUT") &&
              classification &&
              classification !== "NOT_APPLICABLE" &&
              !String(classification).includes("ON_TIME")
            ) {
              return t(`attendance.issueClassification.${classification}`, {
                defaultValue: formatActionType(classification),
              });
            }
            return getActionLabel(actionType);
          })();

          const locationRowKey =
            actionType === "CLOCK_IN"
              ? "clockInLocation"
              : actionType === "CLOCK_OUT"
                ? "clockOutLocation"
                : actionType === "BREAK_START"
                  ? "breakStartLocation"
                  : actionType === "BREAK_END"
                    ? "breakEndLocation"
                    : "punchLocation";

          const scheduledWindowPunch = (() => {
            const ref = scheduleStatus?.scheduleReference;
            const timing = punch?.schedule_timing;
            const startH =
              ref?.scheduledStart ??
              ref?.scheduled_start ??
              timing?.scheduleStart ??
              timing?.schedule_start ??
              punchLite?.schedule_timing?.scheduleStart ??
              record?.details?.scheduledStart ??
              schedRoot?.startTime;
            const endH =
              ref?.scheduledEnd ??
              ref?.scheduled_end ??
              timing?.scheduleEnd ??
              timing?.schedule_end ??
              punchLite?.schedule_timing?.scheduleEnd ??
              record?.details?.scheduledEnd ??
              schedRoot?.endTime;
            const grace =
              ref?.graceMinutes ??
              ref?.grace_minutes ??
              timing?.graceMinutes ??
              timing?.grace_minutes ??
              punchLite?.schedule_timing?.graceMinutes ??
              settings?.schedule?.graceMinutes;
            if (!startH || !endH) return null;

            const scheduleDate = extractPunchScheduleYmd(record, punch);
            if (!scheduleDate) return null;

            const startStr = formatUtcScheduleTime(scheduleDate, startH);
            const endStr = formatUtcScheduleTime(scheduleDate, endH);
            if (startStr === "--" && endStr === "--") return null;

            return t("attendance.todayPunchDetails.scheduledWindow", {
              start: startStr,
              end: endStr,
              grace: grace ?? "--",
            });
          })();

          const attendanceIssueLabel =
            classification && classification !== "NOT_APPLICABLE"
              ? getAttendanceIssueLabel(classification)
              : null;

          const selfieUrl =
            compRec?.faceScanData?.evidence?.capturedImageUrl ||
            punchLite?.evidencePhoto ||
            punch.evidence?.selfie_url ||
            null;

          const evidenceLocationText =
            compRec?.locationData?.textDescription || getPunchLocationText(punch);

          const validationData = compRec?.validation || {};
          const legacyClass = punch.schedule_timing?.classification;
          const legacyIssueLabel =
            !classification && legacyClass && legacyClass !== "NOT_APPLICABLE"
              ? getAttendanceIssueLabel(legacyClass)
              : null;

          const punchStatusKey = getPunchActivityStatus(punch) || actionType;
          const punchHeaderLabel =
            punchStatusKey && punchStatusKey !== actionType
              ? getAttendanceIssueLabel(punchStatusKey)
              : getActionLabel(actionType);

          return (
            <React.Fragment key={punch.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 2 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 1,
                    backgroundColor: getAttendanceStatusDotColor(
                      getPunchActivityStatus(punch) || actionType,
                      actionType
                    ),
                    flexShrink: 0,
                  }}
                />
                <CustomText
                  sx={{
                    ...textStyles.h1,
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#172B4D",
                    textAlign: "left"
                  }}
                >
                  {punchHeaderLabel}
                </CustomText>
              </Box>
              <InfoBox>
                <InfoRow
                  label={t("summaryDetails.status")}
                  value={
                    <Box sx={{ pointerEvents: "none", display: "flex", justifyContent: "flex-end" }}>
                      <StatusDropdown
                        currentStatus={punchStatusKey}
                      />
                    </Box>
                  }
                />
                <InfoRow label={primaryTimeLabel} value={punchTimeDisplay} />
                <InfoRow
                  label={t(`attendance.todayPunchDetails.${locationRowKey}`)}
                  value={evidenceLocationText || "--"}
                />
                {scheduleStatus ? (
                  <InfoRow
                    label={t("attendance.todayPunchDetails.scheduleMatch")}
                    value={
                      scheduleMatchValid === null ? (
                        "--"
                      ) : (
                        <ValidationBadge isValid={scheduleMatchValid} t={t} />
                      )
                    }
                  />
                ) : null}
                {showClockScheduleRows && attendanceIssueLabel ? (
                  <InfoRow label={t("summaryDetails.attendanceIssue")} value={attendanceIssueLabel} />
                ) : legacyIssueLabel ? (
                  <InfoRow label={t("summaryDetails.attendanceIssue")} value={legacyIssueLabel} />
                ) : null}
                {showMinutesVsSchedule ? (
                  <InfoRow
                    label={t("attendance.todayPunchDetails.minutesVsSchedule")}
                    value={minutesVsText}
                  />
                ) : null}
                {showClockScheduleRows && scheduledWindowPunch ? (
                  <InfoRow
                    label={t("attendance.todayPunchDetails.scheduledWindowLabel")}
                    value={scheduledWindowPunch}
                  />
                ) : null}
                {hasPunchIssues && issueCountDisplay != null ? (
                  <InfoRow
                    label={t("attendance.todayPunchDetails.issueCount")}
                    value={String(issueCountDisplay)}
                  />
                ) : null}
                {hasPunchIssues && issuesLine ? (
                  <InfoRow label={t("issuesDetails")} value={issuesLine} />
                ) : null}
          
                {scheduleIssueList?.length ? (
                  <InfoRow
                    label={t("summaryDetails.attendanceIssue")}
                    value={formatIssueKeysList(scheduleIssueList)}
                  />
                ) : null}
                <InfoRow
                  label={t("attendanceDrawer.selfie")}
                  value={<EvidenceThumb src={selfieUrl} alt={t("attendanceDrawer.selfie")} />}
                />
                <InfoRow
                  label={t("summaryDetails.faceMatched")}
                  value={
                    <ValidationBadge
                      isValid={
                        compRec?.faceScanData?.verification?.matched === true ||
                        punch.verification?.face_matched === true
                      }
                      t={t}
                    />
                  }
                />
                <InfoRow
                  label={t("summaryDetails.locationValidated")}
                  value={
                    <ValidationBadge
                      isValid={
                        compRec?.locationData?.validation?.isValidated === true ||
                        punch.location?.location_validated === true
                      }
                      t={t}
                    />
                  }
                />
                <InfoRow
                  label={t("summaryDetails.reviewStatus")}
                  value={
                    <Box sx={{ pointerEvents: "none" }}>
                      <StatusDropdown
                        currentStatus={mapAttendanceReviewStatus(
                          validationData.reviewStatus ||
                            punch.verification?.review_status ||
                            "--"
                        )}
                      />
                    </Box>
                  }
                />
                <InfoRow
                  label={t("summaryDetails.reviewNote")}
                  value={validationData.reviewNote || "--"}
                />

              </InfoBox>
            </React.Fragment>
          );
        })}

        <SectionHeader label={t("attendanceDrawer.mapView")} />
        <InfoBox>
          <Box sx={{ position: "relative" }}>
            <GoogleMap
              mapContainerStyle={{ height: "300px", width: "100%" }}
              center={markers[0]?.position || defaultCenter}
              zoom={markers.length > 0 ? defaultZoom : fallbackZoom}
              options={{ streetViewControl: false, mapTypeControl: false }}
              onLoad={(map) => {
                previewMapRef.current = map;
                fitMapToMarkers(map);
              }}
            >
              {markers.map((marker) => (
                <Marker
                  key={marker.id}
                  position={marker.position}
                  icon={getMarkerIcon(marker.actionType, marker.statusKey)}
                  onClick={() => setActiveMarkerId(marker.id)}
                >
                  {activeMarkerId === marker.id && (
                    <InfoWindow onCloseClick={() => setActiveMarkerId(null)}>
                      <div>
                        <b>{getMarkerInfoLabel(marker)}</b>
                        <br />
                        {marker.time}
                        <br />
                        {marker.locationText}
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              ))}
            </GoogleMap>
            <CustomButton
              onClick={() => setIsFullMapOpen(true)}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: "#006EC2",
                color: "white",
                fontSize: "12px",
                padding: "4px 10px",
                borderRadius: "6px",
                zIndex: 1000,
              }}
              label={
                <>
                  {t("dashboard.viewFullMap")}
                  <Box
                    component="img"
                    src={maparrow}
                    alt="view icon"
                    sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }}
                  />
                </>
              }
            />
          </Box>
        </InfoBox>
      </Box>

      {isFullMapOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
            backgroundColor: "white",
          }}
        >
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          <GoogleMap
            mapContainerStyle={{ height: "100%", width: "100%" }}
            center={markers[0]?.position || defaultCenter}
            zoom={markers.length > 0 ? defaultZoom : fallbackZoom}
            options={{ streetViewControl: false, mapTypeControl: false }}
            onLoad={(map) => {
              fullMapRef.current = map;
              fitMapToMarkers(map);
            }}
          >
            {markers.map((marker) => (
              <Marker
                key={`full-${marker.id}`}
                position={marker.position}
                icon={getMarkerIcon(marker.actionType, marker.statusKey)}
                onClick={() => setActiveMarkerId(marker.id)}
              >
                {activeMarkerId === marker.id && (
                  <InfoWindow onCloseClick={() => setActiveMarkerId(null)}>
                    <div>
                      <b>{getMarkerInfoLabel(marker)}</b>
                      <br />
                      {marker.time}
                      <br />
                      {marker.locationText}
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            ))}
          </GoogleMap>
        </Box>
      )}
    </Drawer>
  );
}
